import React, { useEffect, useRef, useState } from "react";
import {
  Brain,
  GraduationCap,
  CheckCircle2,
  Menu,
  X,
  Download,
  Monitor,
  WifiOff,
  KeyRound,
  MessageCircle,
  Calculator,
  BookOpen,
  PencilLine,
  LineChart,
  Layers,
  ShieldCheck,
  Timer,
  Bookmark,
  HardDrive,
} from "lucide-react";
import { Cloud, StarBurst, SparkleDoodle, Blob } from "./graphics";

/* ------------------------------------------------------------------ */
/*  Tokens (same as Homepage / Reseller)                                */
/* ------------------------------------------------------------------ */

const INK = "#181611";
const CREAM = "#FDF8F0";
const CREAM_CARD = "#FFF4DE";
const MINT = "#DCEEE3";
const MINT_DEEP = "#BFDFCE";
const PEACH_1 = "#F8CE85";
const PEACH_2 = "#F4AE59";
const ORANGE = "#EE7C3B";
const ORANGE_DEEP = "#D9642A";
const INDIGO = "#3D4EAD";
const FOREST = "#2F6B4F";
const MUTED = "#5B5748";
const LINE = "#EADFC4";

const WHATSAPP_NUMBER = "2347044257292";
const waLink = (msg) =>
  `https://wa.me/${WHATSAPP_NUMBER}?text=${encodeURIComponent(msg)}`;

/* ------------------------------------------------------------------ */
/*  Downloads — edit links here                                           */
/* ------------------------------------------------------------------ */

const DOWNLOADS = {
  jamb: {
    id: "jamb",
    name: "JAMB Desktop CBT",
    windows:
      "https://downloads.kinteron.com/jamb/1.3.2/KINTERON.JAMB.CBT_1.3.2_x64-setup.exe",
    mac: "https://downloads.kinteron.com/jamb/1.3.2/KINTERON.JAMB.CBT_1.3.2_universal.dmg",
    sizeNote: "Windows 7+ · All macOS versions",
  },
  waec: {
    id: "waec",
    name: "WAEC Desktop CBT",
    windows:
      "https://downloads.kinteron.com/waec/1.1.5/KINTERON.WAEC.CBT_1.1.5_x64-setup.exe",
    mac: "https://downloads.kinteron.com/waec/1.1.5/KINTERON.WAEC.CBT_1.1.5_universal.dmg",
    sizeNote: "Windows 7+ · All macOS versions",
  },
};

const CHECKLIST = [
  "2026 CBT questions included",
  "Latest JAMB & WAEC syllabus for 2027 admissions",
  "Official JAMB Novel summary included",
  "Activate once, use for the life of your computer — very cheap",
  "90,000+ offline past questions with solutions, for JAMB and WAEC",
  "Works fully offline, even in remote and rural areas",
  "Study past questions year by year, from 2000 to 2026",
  "Bookmark questions to study later",
  "Exam history and performance analysis",
  "Review all your past exam attempts",
  "WAEC & WASSCE Theory and Objectives included",
  "Official JAMB calculator included",
  "Practice using official JAMB keys — arrow keys, A/B/C/D, S to submit",
  "Carry every subject's past questions on one computer",
  "Practice the exact CBT environment with custom settings",
  "AI answer verifier on every question",
];

/* ------------------------------------------------------------------ */
/*  Small utilities (mirrors Reseller)                                  */
/* ------------------------------------------------------------------ */

function useScrollReveal() {
  const ref = useRef(null);
  const [visible, setVisible] = useState(false);
  useEffect(() => {
    const el = ref.current;
    if (!el) return;
    const obs = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          setVisible(true);
          obs.disconnect();
        }
      },
      { threshold: 0.12 }
    );
    obs.observe(el);
    return () => obs.disconnect();
  }, []);
  return [ref, visible];
}

function Reveal({ children, delay = 0 }) {
  const [ref, visible] = useScrollReveal();
  return (
    <div
      ref={ref}
      style={{
        opacity: visible ? 1 : 0,
        transform: visible ? "translateY(0)" : "translateY(16px)",
        transition: `opacity 0.6s cubic-bezier(.2,.7,.3,1) ${delay}ms, transform 0.6s cubic-bezier(.2,.7,.3,1) ${delay}ms`,
      }}
    >
      {children}
    </div>
  );
}

function HighlightWord({ children, bg = ORANGE, color = "#FFFFFF", rotate = "-1.2deg" }) {
  return (
    <span
      style={{
        display: "inline-block",
        background: bg,
        color,
        padding: "2px 18px 8px",
        borderRadius: "40% 45% 42% 48% / 55% 48% 52% 45%",
        transform: `rotate(${rotate})`,
        position: "relative",
      }}
    >
      {children}
    </span>
  );
}

function Pill({ children }) {
  return (
    <span
      style={{
        display: "inline-flex",
        alignItems: "center",
        fontFamily: "'Space Grotesk', sans-serif",
        fontSize: "12px",
        fontWeight: 600,
        color: MUTED,
        background: CREAM_CARD,
        border: `1.5px solid ${LINE}`,
        borderRadius: "999px",
        padding: "6px 16px",
      }}
    >
      {children}
    </span>
  );
}

/* ------------------------------------------------------------------ */
/*  Download card                                                       */
/* ------------------------------------------------------------------ */

function DownloadCard({ app, tone, icon: Icon }) {
  const accent = tone === "jade" ? FOREST : ORANGE_DEEP;
  const accentBg = tone === "jade" ? MINT : "#FCE9E0";
  return (
    <div
      style={{
        background: "#FFFFFF",
        borderRadius: "22px",
        overflow: "hidden",
        boxShadow: "0 18px 40px rgba(24,22,17,0.12)",
        display: "flex",
        flexDirection: "column",
      }}
    >
      <div style={{ background: INK, padding: "22px 26px" }}>
        <div style={{ display: "flex", alignItems: "center", gap: "14px" }}>
          <div
            style={{
              width: "48px",
              height: "48px",
              borderRadius: "14px",
              background: accentBg,
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              flexShrink: 0,
            }}
          >
            <Icon size={24} color={accent} strokeWidth={1.8} />
          </div>
          <div style={{ flex: 1 }}>
            <div
              style={{
                fontFamily: "'Baloo 2', sans-serif",
                fontWeight: 700,
                fontSize: "19px",
                color: CREAM,
                lineHeight: 1.2,
              }}
            >
              {app.name}
            </div>
            <div
              style={{
                fontFamily: "'IBM Plex Mono', monospace",
                fontSize: "11.5px",
                color: "rgba(253,248,240,0.7)",
                marginTop: "2px",
              }}
            >
              {app.sizeNote}
            </div>
          </div>
        </div>
      </div>

      <div style={{ padding: "24px 24px 26px", display: "flex", flexDirection: "column", gap: "10px", flex: 1 }}>
        <a
          href={app.windows}
          style={{
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            gap: "9px",
            width: "100%",
            background: INK,
            color: CREAM,
            fontFamily: "'Space Grotesk', sans-serif",
            fontWeight: 700,
            fontSize: "14.5px",
            padding: "14px 16px",
            borderRadius: "12px",
            textDecoration: "none",
          }}
        >
          <Monitor size={17} />
          Download for Windows (.exe)
        </a>
        <a
          href={app.mac}
          style={{
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            gap: "9px",
            width: "100%",
            background: CREAM_CARD,
            color: INK,
            fontFamily: "'Space Grotesk', sans-serif",
            fontWeight: 700,
            fontSize: "14.5px",
            padding: "14px 16px",
            borderRadius: "12px",
            border: `1.5px solid ${LINE}`,
            textDecoration: "none",
          }}
        >
          <Download size={17} />
          Download for Mac (.dmg)
        </a>
        <a
          href={waLink(`Hello, I'd like to buy one copy of ${app.name} for my own computer`)}
          target="_blank"
          rel="noopener noreferrer"
          style={{
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            gap: "8px",
            width: "100%",
            background: "#25D366",
            color: "#FFFFFF",
            fontFamily: "'Space Grotesk', sans-serif",
            fontWeight: 700,
            fontSize: "14px",
            padding: "13px 16px",
            borderRadius: "12px",
            textDecoration: "none",
          }}
        >
          <KeyRound size={16} />
          Buy activation code — ₦2,000
        </a>
        <p
          style={{
            fontFamily: "'Space Grotesk', sans-serif",
            fontSize: "12px",
            color: MUTED,
            textAlign: "center",
            margin: "4px 0 0",
            lineHeight: 1.6,
          }}
        >
          Free to download &amp; try · one code (e.g. KINT-XXXX-XXXX-XXXX)
          activates one computer, forever.
        </p>
      </div>
    </div>
  );
}

/* ------------------------------------------------------------------ */
/*  Page                                                                */
/* ------------------------------------------------------------------ */

export default function Desktop() {
  const [navScrolled, setNavScrolled] = useState(false);
  const [mobileOpen, setMobileOpen] = useState(false);
  const downloadRef = useRef(null);

  useEffect(() => {
    const onScroll = () => setNavScrolled(window.scrollY > 30);
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  const scrollToDownload = () =>
    downloadRef.current?.scrollIntoView({ behavior: "smooth", block: "start" });

  return (
    <>
      <style>{`
        @import url('https://fonts.googleapis.com/css2?family=Baloo+2:wght@500;600;700;800&family=Space+Grotesk:wght@400;500;600;700&family=IBM+Plex+Mono:wght@500;600&display=swap');
        *, *::before, *::after { box-sizing: border-box; margin: 0; padding: 0; }
        html { overflow-x: hidden; }
        body { background: ${CREAM}; overflow-x: hidden; max-width: 100vw; }
        .cta-solid {
          display: inline-flex; align-items: center; gap: 9px;
          background: ${ORANGE}; color: #FFFFFF;
          font-family: 'Space Grotesk', sans-serif; font-weight: 700; font-size: 15px;
          padding: 15px 28px; border-radius: 999px; border: none; cursor: pointer;
          text-decoration: none; box-shadow: 0 5px 0 ${ORANGE_DEEP};
          transition: transform 0.15s ease, box-shadow 0.15s ease;
        }
        .cta-solid:hover { transform: translateY(2px); box-shadow: 0 3px 0 ${ORANGE_DEEP}; }
        .cta-ink {
          display: inline-flex; align-items: center; gap: 9px;
          background: ${INK}; color: ${CREAM};
          font-family: 'Space Grotesk', sans-serif; font-weight: 700; font-size: 15px;
          padding: 15px 28px; border-radius: 999px; border: none; cursor: pointer;
          text-decoration: none;
        }
        .cta-line {
          display: inline-flex; align-items: center; gap: 9px;
          background: #FFFFFF; color: ${INK};
          font-family: 'Space Grotesk', sans-serif; font-weight: 700; font-size: 14.5px;
          padding: 13px 22px; border-radius: 999px; border: 1.5px solid ${LINE};
          cursor: pointer; text-decoration: none;
        }
        .cta-line:hover { background: ${CREAM_CARD}; }
        .nav-tab {
          font-family: 'Space Grotesk', sans-serif; font-weight: 600; font-size: 14.5px;
          color: ${INK}; background: transparent; border: none; cursor: pointer;
          padding: 8px 6px; text-decoration: none;
        }
        .nav-tab.active { color: ${ORANGE_DEEP}; border-bottom: 2.5px solid ${ORANGE}; }
        .desktop-card { transition: transform 0.25s ease, box-shadow 0.25s ease; box-shadow: 0 1px 2px rgba(24,22,17,0.05); }
        .desktop-card:hover { transform: translateY(-4px); box-shadow: 0 14px 28px rgba(24,22,17,0.1); }
        @media (max-width: 940px) {
          .nav-links { display: none !important; }
          .nav-cta-desktop { display: none !important; }
          .mobile-menu-btn { display: flex !important; }
          .hero-grid, .why-grid, .steps-grid, .dl-grid, .faq-grid { grid-template-columns: 1fr !important; }
          .checklist-grid { grid-template-columns: 1fr !important; }
        }
      `}</style>

      <div style={{ background: CREAM, minHeight: "100vh", color: INK, overflowX: "hidden" }}>
        {/* NAVBAR */}
        <nav
          style={{
            position: "sticky",
            top: 0,
            zIndex: 100,
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
            padding: "0 24px 0 40px",
            height: "76px",
            background: navScrolled || mobileOpen ? "rgba(253,248,240,0.96)" : "transparent",
            backdropFilter: navScrolled || mobileOpen ? "blur(10px)" : "none",
            borderBottom: navScrolled || mobileOpen ? `1px solid ${LINE}` : "1px solid transparent",
          }}
        >
          <div style={{ display: "flex", alignItems: "center", gap: "36px" }}>
            <a
              href="/"
              style={{
                fontFamily: "'Baloo 2', sans-serif",
                fontWeight: 800,
                fontSize: "23px",
                color: INK,
                textDecoration: "none",
              }}
            >
              Kinteron
            </a>
            <div className="nav-links" style={{ display: "flex", alignItems: "center", gap: "26px" }}>
              <a href="/#apps" className="nav-tab">Apps</a>
              <a href="/desktop" className="nav-tab active">Desktop</a>
              <a href="/desktop#download" className="nav-tab">Download</a>
              <a href="/reseller" className="nav-tab">Reseller</a>
            </div>
          </div>
          <div style={{ display: "flex", alignItems: "center", gap: "10px" }}>
            <button onClick={scrollToDownload} className="cta-solid nav-cta-desktop" style={{ padding: "11px 20px", fontSize: "13.5px" }}>
              <Download size={15} />
              Download now
            </button>
            <button
              className="mobile-menu-btn"
              onClick={() => setMobileOpen((o) => !o)}
              aria-label={mobileOpen ? "Close menu" : "Open menu"}
              style={{
                display: "none",
                alignItems: "center",
                justifyContent: "center",
                width: "40px",
                height: "40px",
                borderRadius: "12px",
                border: "none",
                background: mobileOpen ? INK : CREAM_CARD,
                color: mobileOpen ? CREAM : INK,
                cursor: "pointer",
              }}
            >
              {mobileOpen ? <X size={20} /> : <Menu size={20} />}
            </button>
          </div>
        </nav>

        {mobileOpen && (
          <div
            style={{
              position: "sticky",
              top: "76px",
              zIndex: 99,
              background: "#FFFFFF",
              borderBottom: `1px solid ${LINE}`,
              padding: "8px 20px 20px",
              display: "flex",
              flexDirection: "column",
              gap: "4px",
            }}
          >
            {[
              { label: "Apps", href: "/#apps" },
              { label: "Desktop (you are here)", href: "/desktop" },
              { label: "Download", href: "/desktop#download" },
              { label: "Become a Reseller", href: "/reseller" },
            ].map((l) => (
              <a key={l.label} href={l.href} className="nav-tab" style={{ padding: "14px 8px" }}>
                {l.label}
              </a>
            ))}
            <button onClick={() => { setMobileOpen(false); scrollToDownload(); }} className="cta-solid" style={{ justifyContent: "center", marginTop: "8px" }}>
              <Download size={15} />
              Download now
            </button>
          </div>
        )}

        {/* HERO */}
        <section
          style={{
            background: `linear-gradient(180deg, ${PEACH_1} 0%, ${PEACH_2} 100%)`,
            padding: "64px 40px 0",
            position: "relative",
            overflow: "hidden",
          }}
        >
          <Cloud width={150} style={{ top: "30px", left: "4%" }} />
          <Cloud width={90} opacity={0.6} style={{ top: "120px", right: "8%" }} />
          <StarBurst size={26} color="#FFFFFF" className="twinkle-slow" style={{ top: "60px", left: "22%" }} />
          <SparkleDoodle size={22} color="#D9642A" className="twinkle-slow" style={{ top: "90px", right: "24%" }} />

          <div
            className="hero-grid"
            style={{
              maxWidth: "1180px",
              margin: "0 auto",
              display: "grid",
              gridTemplateColumns: "1.05fr 0.95fr",
              gap: "40px",
              alignItems: "center",
              position: "relative",
              zIndex: 1,
            }}
          >
            <div style={{ paddingBottom: "70px" }}>
              <Pill>🖥️ Windows + Mac · works fully offline</Pill>
              <h1
                style={{
                  fontFamily: "'Baloo 2', sans-serif",
                  fontWeight: 700,
                  fontSize: "clamp(38px, 5vw, 58px)",
                  lineHeight: 1.08,
                  margin: "22px 0 8px",
                  color: INK,
                }}
              >
                Desktop CBT,
                <br />
                <HighlightWord bg={INK} color={CREAM}>built to run offline</HighlightWord>
              </h1>
              <p
                style={{
                  fontFamily: "'Space Grotesk', sans-serif",
                  fontSize: "16px",
                  color: "#3B3728",
                  lineHeight: 1.65,
                  margin: "16px 0 30px",
                  maxWidth: "460px",
                }}
              >
                Two standalone installers — JAMB and WAEC — with
                90,000+ past questions, real exam simulation and an AI tutor.
                Download free, activate once with a ₦2,000 code, use forever.
              </p>
              <div style={{ display: "flex", gap: "14px", flexWrap: "wrap" }}>
                <button className="cta-solid" onClick={scrollToDownload}>
                  <Download size={16} />
                  Download free
                </button>
                <a
                  href={waLink("Hello, I'd like to buy a Desktop CBT activation code for my own computer")}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="cta-line"
                >
                  <KeyRound size={16} />
                  Buy activation code
                </a>
              </div>
              <div style={{ display: "flex", gap: "22px", marginTop: "28px", flexWrap: "wrap" }}>
                {[
                  { n: "90,000+", l: "offline past questions" },
                  { n: "2000–2026", l: "years covered" },
                  { n: "₦2,000", l: "one-time, per computer" },
                ].map((s) => (
                  <div key={s.l}>
                    <div style={{ fontFamily: "'Baloo 2', sans-serif", fontWeight: 700, fontSize: "22px", color: INK }}>
                      {s.n}
                    </div>
                    <div style={{ fontFamily: "'Space Grotesk', sans-serif", fontSize: "12.5px", color: "#3B3728" }}>
                      {s.l}
                    </div>
                  </div>
                ))}
              </div>
            </div>

            {/* Hero side card — quick download */}
            <div style={{ display: "flex", justifyContent: "center", paddingBottom: "70px" }}>
              <div
                style={{
                  width: "min(360px, 100%)",
                  background: "#FFFFFF",
                  borderRadius: "22px",
                  padding: "24px",
                  boxShadow: "0 24px 48px rgba(24,22,17,0.16)",
                  transform: "rotate(1.5deg)",
                  position: "relative",
                }}
              >
                <div
                  style={{
                    display: "flex",
                    justifyContent: "space-between",
                    alignItems: "center",
                    marginBottom: "14px",
                    paddingBottom: "12px",
                    borderBottom: `1.5px dashed ${LINE}`,
                  }}
                >
                  <span style={{ fontFamily: "'IBM Plex Mono', monospace", fontSize: "11px", color: FOREST, fontWeight: 600 }}>
                    FREE DOWNLOAD
                  </span>
                  <span style={{ fontFamily: "'IBM Plex Mono', monospace", fontSize: "11px", color: MUTED }}>
                    WIN + MAC
                  </span>
                </div>
                {[
                  { k: "JAMB Desktop", v: ".exe / .dmg", sub: "Windows + Mac universal" },
                  { k: "WAEC Desktop", v: ".exe / .dmg", sub: "Windows + Mac universal" },
                  { k: "Activation", v: "₦2,000", sub: "one code per machine, forever", green: true },
                ].map((r) => (
                  <div
                    key={r.k}
                    style={{
                      display: "flex",
                      justifyContent: "space-between",
                      alignItems: "center",
                      padding: "12px 14px",
                      borderRadius: "12px",
                      marginBottom: "8px",
                      background: r.green ? MINT : "#FAF8F2",
                      border: r.green ? `1.5px solid ${FOREST}` : `1.5px solid ${LINE}`,
                    }}
                  >
                    <span>
                      <span style={{ display: "block", fontFamily: "'Space Grotesk', sans-serif", fontSize: "12px", color: MUTED, fontWeight: 600 }}>
                        {r.k}
                      </span>
                      <span style={{ display: "block", fontFamily: "'Space Grotesk', sans-serif", fontSize: "11px", color: MUTED }}>
                        {r.sub}
                      </span>
                    </span>
                    <span style={{ fontFamily: "'Baloo 2', sans-serif", fontWeight: 700, fontSize: "18px", color: r.green ? FOREST : INK }}>
                      {r.v}
                    </span>
                  </div>
                ))}
                <div style={{ marginTop: "12px", padding: "10px 12px", borderRadius: "10px", background: CREAM_CARD, display: "flex", gap: "8px", alignItems: "center" }}>
                  <WifiOff size={15} color={ORANGE_DEEP} />
                  <span style={{ fontFamily: "'Space Grotesk', sans-serif", fontSize: "11.5px", color: "#3B3728" }}>
                    After install, no internet needed — ever.
                  </span>
                </div>
              </div>
            </div>
          </div>

          <svg viewBox="0 0 1440 80" preserveAspectRatio="none" style={{ width: "100%", height: "60px", display: "block", marginTop: "10px" }}>
            <path d="M0,40 C240,90 480,0 720,20 C960,40 1200,90 1440,30 L1440,80 L0,80 Z" fill={MINT} />
          </svg>
        </section>

        {/* DOWNLOAD */}
        <section id="download" ref={downloadRef} style={{ background: MINT, padding: "20px 40px 90px", scrollMarginTop: "80px" }}>
          <div style={{ maxWidth: "960px", margin: "0 auto" }}>
            <Reveal>
              <div style={{ textAlign: "center", marginBottom: "32px" }}>
                <span style={{ fontFamily: "'IBM Plex Mono', monospace", fontSize: "12px", color: "#33512F", letterSpacing: "0.06em" }}>
                  FREE DOWNLOAD · PAY ONCE TO ACTIVATE
                </span>
                <h2 style={{ fontFamily: "'Baloo 2', sans-serif", fontWeight: 700, fontSize: "clamp(28px, 3.6vw, 42px)", color: INK, marginTop: "12px" }}>
                  Download for <HighlightWord bg={FOREST} color={CREAM} rotate="1deg">your computer</HighlightWord>
                </h2>
                <p style={{ fontFamily: "'Space Grotesk', sans-serif", fontSize: "15px", color: MUTED, maxWidth: "540px", margin: "16px auto 0", lineHeight: 1.7 }}>
                  Pick your exam, pick your system. Install, then buy one ₦2,000
                  activation code on WhatsApp to unlock everything.
                </p>
              </div>
            </Reveal>
            <div className="dl-grid" style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: "22px" }}>
              <Reveal>
                <DownloadCard app={DOWNLOADS.jamb} tone="jade" icon={Brain} />
              </Reveal>
              <Reveal delay={90}>
                <DownloadCard app={DOWNLOADS.waec} tone="orange" icon={GraduationCap} />
              </Reveal>
            </div>
            <Reveal delay={140}>
              <div
                style={{
                  marginTop: "20px",
                  background: "#FFFFFF",
                  border: `1.5px solid ${LINE}`,
                  borderRadius: "16px",
                  padding: "18px 22px",
                  display: "flex",
                  gap: "12px",
                  alignItems: "flex-start",
                }}
              >
                <HardDrive size={20} color={FOREST} style={{ flexShrink: 0, marginTop: "2px" }} />
                <p style={{ fontFamily: "'Space Grotesk', sans-serif", fontSize: "13px", color: MUTED, lineHeight: 1.65, margin: 0 }}>
                  <strong style={{ color: INK }}>Not sure which file?</strong> Windows laptops use the{" "}
                  <strong style={{ color: INK }}>.exe</strong> installer. MacBooks (Intel &amp; Apple Silicon) use the{" "}
                  <strong style={{ color: INK }}>.dmg</strong> universal build. Need help installing?{" "}
                  <a href={waLink("Hello Kinteron, I need help installing the Desktop CBT on my computer")} target="_blank" rel="noopener noreferrer" style={{ color: FOREST, fontWeight: 700 }}>
                    Chat with us on WhatsApp
                  </a>
                  .
                </p>
              </div>
            </Reveal>
          </div>
        </section>

        {/* FEATURES */}
        <section style={{ background: CREAM, padding: "90px 40px" }}>
          <div style={{ maxWidth: "1180px", margin: "0 auto" }}>
            <Reveal>
              <div style={{ textAlign: "center", marginBottom: "48px" }}>
                <span style={{ fontFamily: "'IBM Plex Mono', monospace", fontSize: "12px", color: ORANGE_DEEP, letterSpacing: "0.06em" }}>
                  WHAT'S INSIDE
                </span>
                <h2 style={{ fontFamily: "'Baloo 2', sans-serif", fontWeight: 700, fontSize: "clamp(28px, 3.8vw, 44px)", color: INK, marginTop: "12px" }}>
                  Everything on <HighlightWord bg={INDIGO} rotate="-1deg">desktop</HighlightWord>
                </h2>
                <p style={{ fontFamily: "'Space Grotesk', sans-serif", fontSize: "15px", color: MUTED, maxWidth: "520px", margin: "16px auto 0", lineHeight: 1.7 }}>
                  The same CBT engine tutorial centres run — now on your own
                  computer, with no data cost after install.
                </p>
              </div>
            </Reveal>
            <div className="why-grid" style={{ display: "grid", gridTemplateColumns: "repeat(3, 1fr)", gap: "22px" }}>
              {[
                { icon: WifiOff, title: "100% offline after install", body: "Download once, study forever without internet. Built for homes, villages and centres with poor network.", tone: FOREST },
                { icon: Timer, title: "Real exam simulation", body: "Official timer, JAMB calculator and real exam keys — arrow keys, A/B/C/D, S to submit — plus custom settings.", tone: ORANGE },
                { icon: BookOpen, title: "90,000+ past questions", body: "Every subject, year by year from 2000 to 2026, with WAEC Theory + Objectives and the official JAMB Novel summary.", tone: INDIGO },
                { icon: ShieldCheck, title: "AI answer verifier", body: "Every answer is checked by AI with full step-by-step explanations — you learn the why, not just the score.", tone: ORANGE_DEEP },
                { icon: PencilLine, title: "Topic-by-topic + full mocks", body: "Drill weak topics one by one, then sit full timed simulations that feel exactly like exam day.", tone: FOREST },
                { icon: LineChart, title: "History, analytics & bookmarks", body: "Every attempt saved with performance analysis. Bookmark hard questions, replay past exams, watch scores climb.", tone: INDIGO },
                { icon: Layers, title: "Flashcards for quick revision", body: "Bite-size flashcards sourced from real past questions — perfect for daily streaks and last-minute revision.", tone: FOREST },
                { icon: Bookmark, title: "Study your way", body: "Carry every subject on one computer. Filter by year, topic or difficulty and revise at your own pace.", tone: ORANGE },
                { icon: Monitor, title: "Windows + Mac installers", body: "Separate JAMB and WAEC apps for Windows 7+ and all macOS versions — universal build for Intel and Apple Silicon.", tone: INDIGO },
              ].map((c, i) => {
                const Icon = c.icon;
                return (
                  <Reveal key={c.title} delay={(i % 3) * 70}>
                    <div className="desktop-card" style={{ background: "#FFFFFF", borderRadius: "22px", padding: "28px 24px", height: "100%", position: "relative" }}>
                      <Blob width={110} opacity={0.3} style={{ bottom: "-18px", right: "-20px" }} />
                      <div style={{ width: "48px", height: "48px", borderRadius: "14px", background: c.tone, display: "flex", alignItems: "center", justifyContent: "center", marginBottom: "16px" }}>
                        <Icon size={22} color="#FFFFFF" strokeWidth={1.9} />
                      </div>
                      <h3 style={{ fontFamily: "'Baloo 2', sans-serif", fontWeight: 600, fontSize: "18px", color: INK, marginBottom: "8px" }}>
                        {c.title}
                      </h3>
                      <p style={{ fontFamily: "'Space Grotesk', sans-serif", fontSize: "13.5px", color: MUTED, lineHeight: 1.65 }}>
                        {c.body}
                      </p>
                    </div>
                  </Reveal>
                );
              })}
            </div>
          </div>
        </section>

        {/* FULL CHECKLIST */}
        <section style={{ background: MINT, padding: "90px 40px" }}>
          <div style={{ maxWidth: "1000px", margin: "0 auto" }}>
            <Reveal>
              <div style={{ textAlign: "center", marginBottom: "36px" }}>
                <span style={{ fontFamily: "'IBM Plex Mono', monospace", fontSize: "12px", color: "#33512F", letterSpacing: "0.06em" }}>
                  THE FULL LIST
                </span>
                <h2 style={{ fontFamily: "'Baloo 2', sans-serif", fontWeight: 700, fontSize: "clamp(26px, 3.4vw, 38px)", color: INK, marginTop: "12px" }}>
                  What's included, in full
                </h2>
              </div>
            </Reveal>
            <Reveal delay={80}>
              <div style={{ background: "#FFFFFF", borderRadius: "22px", padding: "32px 34px", boxShadow: "0 1px 3px rgba(24,22,17,0.06)" }}>
                <div className="checklist-grid" style={{ display: "grid", gridTemplateColumns: "repeat(2, 1fr)", columnGap: "28px", rowGap: "13px" }}>
                  {CHECKLIST.map((item) => (
                    <div key={item} style={{ display: "flex", alignItems: "flex-start", gap: "9px" }}>
                      <CheckCircle2 size={16} color={FOREST} strokeWidth={2} style={{ flexShrink: 0, marginTop: "1.5px" }} />
                      <span style={{ fontFamily: "'Space Grotesk', sans-serif", fontSize: "13.5px", color: "#33321f", lineHeight: 1.5 }}>
                        {item}
                      </span>
                    </div>
                  ))}
                </div>
                <p style={{ fontFamily: "'Space Grotesk', sans-serif", fontSize: "13px", color: MUTED, fontStyle: "italic", marginTop: "22px", paddingTop: "18px", borderTop: `1px solid ${LINE}` }}>
                  With constant practice on Kinteron JAMB CBT, you're set up to score 300+ in your JAMB CBT exam.
                </p>
              </div>
            </Reveal>
          </div>
        </section>

        {/* HOW ACTIVATION WORKS */}
        <section style={{ background: CREAM, padding: "90px 40px" }}>
          <div style={{ maxWidth: "1180px", margin: "0 auto" }}>
            <Reveal>
              <div style={{ textAlign: "center", marginBottom: "48px" }}>
                <span style={{ fontFamily: "'IBM Plex Mono', monospace", fontSize: "12px", color: ORANGE_DEEP, letterSpacing: "0.06em" }}>
                  HOW IT WORKS
                </span>
                <h2 style={{ fontFamily: "'Baloo 2', sans-serif", fontWeight: 700, fontSize: "clamp(28px, 3.8vw, 44px)", color: INK, marginTop: "12px" }}>
                  Download today, <HighlightWord bg={ORANGE}>study tonight</HighlightWord>
                </h2>
              </div>
            </Reveal>
            <div className="steps-grid" style={{ display: "grid", gridTemplateColumns: "repeat(3, 1fr)", gap: "22px" }}>
              {[
                { icon: Download, num: "1.", title: "Download & install", body: "Grab the free JAMB or WAEC installer above — .exe for Windows, .dmg for Mac. Runs on Windows 7+ and all macOS versions." },
                { icon: KeyRound, num: "2.", title: "Buy your code — ₦2,000", body: "Message us on WhatsApp and get a KINT-XXXX-XXXX-XXXX code in minutes. One code activates one computer, for life." },
                { icon: Calculator, num: "3.", title: "Activate & practise offline", body: "Enter the code once in the app. From then on everything — questions, AI explanations, mocks — works with zero internet." },
              ].map((s, i) => {
                const Icon = s.icon;
                return (
                  <Reveal key={s.num} delay={i * 100}>
                    <div style={{ background: "#FFFFFF", borderRadius: "22px", padding: "30px 26px", height: "100%", position: "relative", textAlign: "center" }}>
                      <div style={{ width: "56px", height: "56px", borderRadius: "18px", background: INK, display: "flex", alignItems: "center", justifyContent: "center", margin: "0 auto 16px" }}>
                        <Icon size={24} color={CREAM} strokeWidth={1.8} />
                      </div>
                      <div style={{ fontFamily: "'IBM Plex Mono', monospace", fontSize: "13px", fontWeight: 700, color: ORANGE_DEEP, marginBottom: "6px" }}>
                        STEP {s.num}
                      </div>
                      <h3 style={{ fontFamily: "'Baloo 2', sans-serif", fontWeight: 700, fontSize: "19px", color: INK, marginBottom: "10px" }}>
                        {s.title}
                      </h3>
                      <p style={{ fontFamily: "'Space Grotesk', sans-serif", fontSize: "13.5px", color: MUTED, lineHeight: 1.65 }}>
                        {s.body}
                      </p>
                    </div>
                  </Reveal>
                );
              })}
            </div>
            <Reveal delay={150}>
              <div style={{ display: "flex", justifyContent: "center", marginTop: "36px", gap: "12px", flexWrap: "wrap" }}>
                <button className="cta-solid" onClick={scrollToDownload}>
                  <Download size={16} />
                  Get the installers
                </button>
                <a href="/reseller" className="cta-line">
                  <MessageCircle size={16} />
                  Setting up a centre? Become a reseller
                </a>
              </div>
            </Reveal>
          </div>
        </section>

        {/* FAQ */}
        <section style={{ background: CREAM, padding: "0 40px 90px" }}>
          <div style={{ maxWidth: "1000px", margin: "0 auto" }}>
            <Reveal>
              <div style={{ textAlign: "center", marginBottom: "44px" }}>
                <span style={{ fontFamily: "'IBM Plex Mono', monospace", fontSize: "12px", color: ORANGE_DEEP, letterSpacing: "0.06em" }}>
                  DESKTOP QUESTIONS
                </span>
                <h2 style={{ fontFamily: "'Baloo 2', sans-serif", fontWeight: 700, fontSize: "clamp(28px, 3.8vw, 42px)", color: INK, marginTop: "12px" }}>
                  Before you <HighlightWord bg={FOREST} color={CREAM} rotate="1deg">download</HighlightWord>
                </h2>
              </div>
            </Reveal>
            <div className="faq-grid" style={{ display: "grid", gridTemplateColumns: "repeat(2, 1fr)", gap: "18px" }}>
              {[
                { q: "Is the download really free?", a: "Yes. Both installers are free to download and try. You only pay ₦2,000 once for an activation code that unlocks everything on that computer, forever." },
                { q: "Which file do I download?", a: "Windows laptops: the x64 .exe setup file. MacBooks (Intel and Apple Silicon): the universal .dmg file." },
                { q: "Does it work without internet?", a: "Yes — fully. After install and one-time activation, all 90,000+ questions, AI explanations and mocks work offline, even in remote areas." },
                { q: "One code per computer?", a: "Yes. Each KINT-XXXX-XXXX-XXXX code activates one machine for life. Centres buy one code per computer — see reseller bulk rates from ₦1,000." },
                { q: "What are the system requirements?", a: "Windows 7 or newer (64-bit), or any recent macOS version — the universal build runs on both Intel and Apple Silicon Macs. Any fairly recent laptop with a few GB of free space runs it smoothly." },
                { q: "What if installation or activation fails?", a: "Message us on WhatsApp with a screenshot of the error. We help with installs and replace genuine failed activations." },
              ].map((f, i) => (
                <Reveal key={f.q} delay={i * 60}>
                  <div className="desktop-card" style={{ background: "#FFFFFF", borderRadius: "20px", padding: "24px", height: "100%" }}>
                    <h3 style={{ fontFamily: "'Baloo 2', sans-serif", fontWeight: 600, fontSize: "16.5px", color: INK, marginBottom: "10px", lineHeight: 1.35 }}>
                      {f.q}
                    </h3>
                    <p style={{ fontFamily: "'Space Grotesk', sans-serif", fontSize: "13px", color: MUTED, lineHeight: 1.65 }}>
                      {f.a}
                    </p>
                  </div>
                </Reveal>
              ))}
            </div>
          </div>
        </section>

        {/* FINAL CTA */}
        <section style={{ padding: "0 40px 80px" }}>
          <div
            style={{
              maxWidth: "1000px",
              margin: "0 auto",
              background: INK,
              borderRadius: "26px",
              padding: "56px 48px",
              textAlign: "center",
              position: "relative",
              overflow: "hidden",
            }}
          >
            <StarBurst size={24} color={ORANGE} className="twinkle-slow" style={{ top: "30px", left: "6%" }} />
            <StarBurst size={18} color={ORANGE} className="twinkle-fast" style={{ bottom: "36px", right: "8%" }} />
            <h2 style={{ fontFamily: "'Baloo 2', sans-serif", fontWeight: 700, fontSize: "clamp(26px, 3.6vw, 40px)", color: CREAM, lineHeight: 1.2, marginBottom: "14px" }}>
              Your offline CBT centre,
              <br />
              on your own computer.
            </h2>
            <p style={{ fontFamily: "'Space Grotesk', sans-serif", fontSize: "15px", color: "rgba(253,248,240,0.75)", maxWidth: "480px", margin: "0 auto 30px", lineHeight: 1.65 }}>
              Download JAMB or WAEC free now — activate once for
              ₦2,000 and practise like it's exam day, every day.
            </p>
            <div style={{ display: "flex", gap: "12px", justifyContent: "center", flexWrap: "wrap" }}>
              <button className="cta-solid" onClick={scrollToDownload}>
                <Download size={16} />
                Download free now
              </button>
              <a
                href={waLink("Hello Kinteron, I'd like to buy a Desktop CBT activation code. My name is ___")}
                target="_blank"
                rel="noopener noreferrer"
                className="cta-line"
              >
                <MessageCircle size={16} />
                Buy activation code
              </a>
            </div>
            <div style={{ marginTop: "26px", display: "flex", alignItems: "center", justifyContent: "center", gap: "8px", fontFamily: "'Space Grotesk', sans-serif", fontSize: "12.5px", color: "rgba(253,248,240,0.6)" }}>
              <CheckCircle2 size={14} color="#25D366" />
              Free download · One-time ₦2,000 activation · Works offline forever
            </div>
          </div>
        </section>

        {/* CONTACT STRIP */}
        <section style={{ padding: "0 40px 80px" }}>
          <div style={{ maxWidth: "1000px", margin: "0 auto", display: "flex", gap: "14px", flexWrap: "wrap", justifyContent: "center" }}>
            <a href={waLink("Hello Kinteron, I have a question about the Desktop CBT.")} target="_blank" rel="noopener noreferrer" className="cta-line" style={{ background: "#E7F9EE", borderColor: MINT_DEEP }}>
              <MessageCircle size={16} color={FOREST} />
              WhatsApp: 0704 425 7292
            </a>
            <a href="tel:+2347044257292" className="cta-line">
              <Monitor size={16} />
              Call: 0704 425 7292
            </a>
          </div>
        </section>

        {/* FOOTER */}
        <footer style={{ borderTop: `1px solid ${LINE}`, background: CREAM }}>
          <div style={{ maxWidth: "1180px", margin: "0 auto", padding: "36px 40px", display: "flex", justifyContent: "space-between", alignItems: "center", flexWrap: "wrap", gap: "14px" }}>
            <a href="/" style={{ fontFamily: "'Baloo 2', sans-serif", fontWeight: 700, fontSize: "18px", color: INK, textDecoration: "none" }}>
              Kinteron
            </a>
            <span style={{ fontFamily: "'Space Grotesk', sans-serif", fontSize: "12.5px", color: MUTED, textAlign: "center" }}>
              Kinteron is not affiliated with JAMB, WAEC, NECO, or any government examination body.
            </span>
            <span style={{ fontFamily: "'Space Grotesk', sans-serif", fontSize: "12.5px", color: MUTED }}>
              © 2026 Kinteron. Made in Nigeria 🇳🇬
            </span>
          </div>
        </footer>
      </div>
    </>
  );
}
